// Full site content store — all editable content persisted in localStorage
// Now with full bilingual support (ru / ky)

export interface LocalizedString {
  ru: string;
  ky: string;
}
export interface Product {
  id: string;
  name: LocalizedString;
  subtitle: LocalizedString;
  category: LocalizedString;
  price: number;
  unit: LocalizedString;
  image: string;
  description: LocalizedString;
  advantages: LocalizedString[];
  specs: {
    label: LocalizedString;
    value: LocalizedString;
  }[];
  active: boolean;
  parentLine?: string;
}
export interface PreviewProduct {
  id: number;
  name: LocalizedString;
  category: LocalizedString;
  description: LocalizedString;
  image: string;
}
export interface StatItem {
  value: LocalizedString;
  label: LocalizedString;
  delay: number;
}
export interface MapConfig {
  lat: string;
  lng: string;
  zoom: number;
  addressLabel: LocalizedString;
}
export interface AreaBlock {
  id: string;
  icon: string;
  title: LocalizedString;
  description: LocalizedString;
}
export interface PartnerBenefit {
  id: string;
  icon: string;
  title: LocalizedString;
  description: LocalizedString;
}
export interface PartnerStep {
  id: string;
  num: string;
  title: LocalizedString;
  desc: LocalizedString;
}
export interface PartnerPageContent {
  heroLabel: LocalizedString;
  heroTitle: LocalizedString;
  heroTitleAccent: LocalizedString;
  heroDescription: LocalizedString;
  benefitsLabel: LocalizedString;
  benefitsTitle: LocalizedString;
  benefitsTitleAccent: LocalizedString;
  stepsLabel: LocalizedString;
  stepsTitle: LocalizedString;
  stepsTitleAccent: LocalizedString;
  formLabel: LocalizedString;
  formTitle: LocalizedString;
  formTitleAccent: LocalizedString;
  formDescription: LocalizedString;
}
export interface PartnerSectionContent {
  label: LocalizedString;
  title: LocalizedString;
  titleAccent: LocalizedString;
  description: LocalizedString;
  ctaText: LocalizedString;
}
export interface TeamMember {
  id: string;
  name: LocalizedString;
  role: LocalizedString;
  image: string;
}
export interface HeroContent {
  subtitle: LocalizedString;
  title2: LocalizedString;
  description: LocalizedString;
  btnCatalog: LocalizedString;
  btnContact: LocalizedString;
  hint: LocalizedString;
}
export interface AboutFeature {
  id: string;
  icon: string;
  title: LocalizedString;
  text: LocalizedString;
}
export interface AboutContent {
  label: LocalizedString;
  title1: LocalizedString;
  title2: LocalizedString;
  description: LocalizedString;
  image: string;
  features: AboutFeature[];
  teamTitle1: LocalizedString;
  teamTitle2: LocalizedString;
  teamDesc: LocalizedString;
}
export interface CatalogSectionContent {
  label: LocalizedString;
  title1: LocalizedString;
  title2: LocalizedString;
}
export interface AreasSectionContent {
  label: LocalizedString;
  title1: LocalizedString;
  title2: LocalizedString;
}
export interface ClientItem {
  id: string;
  name: string;
  logoUrl: string;
}
export interface ContentStore {
  products: Product[];
  previewProducts: PreviewProduct[];
  stats: StatItem[];
  map: MapConfig;
  applicationAreas: AreaBlock[];
  partnerBenefits: PartnerBenefit[];
  partnerSteps: PartnerStep[];
  partnerPage: PartnerPageContent;
  partnerSection: PartnerSectionContent;
  teamMembers: TeamMember[];
  hero: HeroContent;
  about: AboutContent;
  catalogSection: CatalogSectionContent;
  areasSection: AreasSectionContent;
  clients: ClientItem[];
}

// Helper to create localized strings easily
const l = (ru: string, ky: string = ru): LocalizedString => ({
  ru,
  ky
});
const DEFAULT_CONTENT: ContentStore = {
  hero: {
    subtitle: l('ОсОО Явуз Бурак — Бишкек', 'ЖЧК Явуз Бурак — Бишкек'),
    title2: l('Фасадные и интерьерные краски европейского качества', 'Европа сапатындагы фасаддык жана интерьердик боёктор'),
    description: l('ОсОО Явуз Бурак — профессиональные лакокрасочные материалы для фасадов и интерьеров. Высокое качество, долговечность и доступная цена.', 'ЖЧК Явуз Бурак — фасаддар жана интерьерлер үчүн кесипкөй лак-боёк материалдары. Жогорку сапат, узак мөөнөттүүлүк жана жеткиликтүү баа.'),
    btnCatalog: l('Каталог продукции', 'Продукция каталогу'),
    btnContact: l('Связаться с нами', 'Биз менен байланышыңыз'),
    hint: l('↓ наведите на цвет, чтобы покрасить', '↓ боёо үчүн түстүн үстүнө барыңыз')
  },
  about: {
    label: l('О компании', 'Компания жөнүндө'),
    title1: l('YAVUZ BURAK —'),
    title2: l('11 ЛЕТ КАЧЕСТВА', '11 ЖЫЛДЫК САПАТ'),
    description: l('ОсОО «Явуз Бурак» — современная производственная компания (основана в 2014 г.), развивающая собственные технологии в области лакокрасочных материалов. Мы создаём продукты линеек STORM PAINT, BM PAINT, GRANIT LINE, которые объединяют эстетичность, технологичность и устойчивость к климатическим условиям.', '«Явуз Бурак» ЖЧК — лак-боёк материалдары тармагында өз технологияларын өнүктүргөн заманбап өндүрүш компаниясы (2014-жылы негизделген). Биз эстетиканы, технологиялуулукту жана климаттык шарттарга туруктуулукту айкалыштырган STORM PAINT, BM PAINT, GRANIT LINE линияларынын продуктуларын жаратабыз.'),
    image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&q=80&w=1000',
    features: [{
      id: 'exp',
      icon: 'Clock',
      title: l('Опыт', 'Тажрыйба'),
      text: l('11 лет на рынке (с 2014 года).', 'Рынокто 11 жыл (2014-жылдан бери).')
    }, {
      id: 'prod',
      icon: 'ShieldCheck',
      title: l('Производство', 'Өндүрүш'),
      text: l('Собственные технологии и рецептуры.', 'Өздүк технологиялар жана рецептуралар.')
    }, {
      id: 'assort',
      icon: 'Award',
      title: l('Ассортимент', 'Ассортимент'),
      text: l('Фасадные, интерьерные краски, декоративные покрытия.', 'Фасаддык, интерьердик боёктор, декоративдик каптоолор.')
    }, {
      id: 'partner',
      icon: 'Users',
      title: l('Партнерство', 'Өнөктөштүк'),
      text: l('Выгодные условия для дилеров (BM PARTNER).', 'Дилерлер үчүн пайдалуу шарттар (BM PARTNER).')
    }],
    teamTitle1: l('Наша', 'Биздин'),
    teamTitle2: l('Команда', 'Команда'),
    teamDesc: l('Профессионалы, стоящие за качеством YAVUZ BURAK', 'YAVUZ BURAK сапатынын артында турган кесипкөйлөр')
  },
  catalogSection: {
    label: l('Каталог продукции', 'Продукция каталогу'),
    title1: l('Популярные', 'Популярдуу'),
    title2: l('решения', 'чечимдер')
  },
  areasSection: {
    label: l('Сферы применения', 'Колдонуу чөйрөлөрү'),
    title1: l('Где работают наши', 'Биздин каптоолор кайда'),
    title2: l('покрытия', 'иштейт')
  },
  clients: [{
    id: 'c1',
    name: 'Имарат Прогресс',
    logoUrl: ''
  }, {
    id: 'c2',
    name: 'Байташ Групп',
    logoUrl: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/hyseuuq2warchstz2tvf3s/f7c74e3b-3f4e-439c-8223-13536fb8e81c/Vector-bRI5ntMy[1].svg"
  }, {
    id: 'c3',
    name: 'Ориент Инвест',
    logoUrl: ''
  }, {
    id: 'c4',
    name: 'Юнипрогресс',
    logoUrl: ''
  }, {
    id: 'c5',
    name: 'Сити Девелопмент',
    logoUrl: ''
  }, {
    id: 'c6',
    name: 'Нур Групп',
    logoUrl: ''
  }, {
    id: 'c7',
    name: 'Арыкан Иншаат',
    logoUrl: ''
  }, {
    id: 'c8',
    name: 'КТУ «Манас»',
    logoUrl: ''
  }, {
    id: 'c9',
    name: 'Эр-Фа Бишкек',
    logoUrl: ''
  }, {
    id: 'c10',
    name: 'Даймонд Сити',
    logoUrl: ''
  }, {
    id: 'c11',
    name: 'Байдар Групп',
    logoUrl: ''
  }, {
    id: 'c12',
    name: 'Борсан Констракшн',
    logoUrl: ''
  }, {
    id: 'c13',
    name: 'Падыша Ата',
    logoUrl: ''
  }, {
    id: 'c14',
    name: 'Фемили Вилладж',
    logoUrl: ''
  }, {
    id: 'c15',
    name: 'Хенкель Бишкек',
    logoUrl: 'https://logo.clearbit.com/henkel.com'
  }, {
    id: 'c16',
    name: 'Эра Констракшн',
    logoUrl: ''
  }, {
    id: 'c17',
    name: 'Бишкек Курулуш',
    logoUrl: ''
  }, {
    id: 'c18',
    name: 'Ак-Таш Строй',
    logoUrl: ''
  }, {
    id: 'c19',
    name: 'Мега Комфорт',
    logoUrl: ''
  }, {
    id: 'c20',
    name: 'Азия Строй',
    logoUrl: ''
  }],
  previewProducts: [{
    id: 1,
    name: l('VELORA'),
    category: l('Интерьерная краска', 'Интерьердик боёк'),
    description: l('Премиум силиконовая краска. Шелковисто-матовая, моющаяся. LUX / BASIC / ECO', 'Премиум силикон боёгу. Жибектей жалтырабаган, жуула турган. LUX / BASIC / ECO'),
    image: "/VELORA_LUX.jpg"
  }, {
    id: 2,
    name: l('EXTERRA'),
    category: l('Фасадная краска', 'Фасаддык боёк'),
    description: l('Силиконовая фасадная краска. Водоотталкивающая, долговечная. LUX / BASIC / ECO', 'Силикон фасаддык боёгу. Сууну түртүүчү, узакка чыдамдуу. LUX / BASIC / ECO'),
    image: "/EXTERRA_LUX.jpg"
  }, {
    id: 3,
    name: l('MICROLUX'),
    category: l('Микроцемент', 'Микроцемент'),
    description: l('Микроцементная краска для стен. Эффект бетона.', 'Дубалдар үчүн микроцемент боёгу. Бетон эффектиси.'),
    image: 'https://images.unsplash.com/photo-1565538810643-b5bdb714032a?auto=format&fit=crop&q=80&w=800'
  }, {
    id: 4,
    name: l('NEO STONE'),
    category: l('Жидкий гранит', 'Суюк гранит'),
    description: l('Декоративное покрытие с эффектом натурального камня.', 'Табигый таш эффектиси бар декоративдик каптоо.'),
    image: 'https://images.unsplash.com/photo-1541123437800-1bb1317badc2?auto=format&fit=crop&q=80&w=800'
  }],
  products: [{
    id: 'velora',
    name: l('VELORA'),
    category: l('Интерьерные краски', 'Интерьердик боёктор'),
    subtitle: l('Интерьерная краска на основе стирол-акрилового сополимера', 'Стирол-акрил сополимеринин негизиндеги интерьердик боёк'),
    price: 4600,
    unit: l('20кг'),
    image: "/VELORA_LUX.jpg",
    description: l('VELORA — высококачественная интерьерная краска на основе стирол-акрилового сополимера, разработанная для долговечного декоративного покрытия стен и потолков. Благодаря современной водно-дисперсионной технологии и усовершенствованной силиконовой формуле краска образует водоотталкивающую, гладкую и однородную матовую поверхность с глубоким оттенком и высокой стойкостью к мытью и износу.', 'VELORA — дубалдарды жана шыптарды узакка чыдамдуу декоративдик каптоо үчүн иштелип чыккан стирол-акрил сополимеринин негизиндеги жогорку сапаттагы интерьердик боёк.'),
    advantages: [l('Силиконовая технология — формирует водоотталкивающий слой и гладкую фактуру', 'Силикон технологиясы — сууну түртүүчү катмарды жана жылмакай фактураны түзөт'), l('Водно-дисперсионная формула — экологически безопасное применение без резкого запаха', 'Суу-дисперсиялык формула — курч жытсыз экологиялык коопсуз колдонуу'), l('Глубокий матовый эффект — эстетичный и современный интерьерный финиш', 'Терең жалтырабаган эффект — эстетикалуу жана заманбап интерьердик финиш'), l('Высокая стойкость к мытью и истиранию', 'Жууганга жана тозууга жогорку туруктуулук'), l('Доступна в версиях LUX, BASIC, ECO', 'LUX, BASIC, ECO версияларында жеткиликтүү')],
    specs: [],
    active: true
  }, {
    id: 'velora-lux',
    name: l('VELORA LUX'),
    category: l('Интерьерные краски', 'Интерьердик боёктор'),
    subtitle: l('Премиальная силиконовая интерьерная краска', 'Премиум силикон интерьердик боёгу'),
    price: 5200,
    unit: l('20кг'),
    image: "/VELORA_LUX.jpg",
    description: l('VELORA LUX — высококачественная интерьерная краска с повышенным содержанием силикона, обеспечивающая максимальную стойкость покрытия к мытью и загрязнениям.', 'VELORA LUX — жууганга жана булганууга каптоонун максималдуу туруктуулугун камсыз кылган жогорку сапаттагы интерьердик боёк.'),
    advantages: [l('Повышенное содержание силикона для максимальной защиты', 'Максималдуу коргоо үчүн жогорулатылган силикон курамы'), l('Шелковисто-матовое покрытие с глубоким цветом', 'Терең түсү бар жибектей жалтырабаган каптоо'), l('Максимальная стойкость к мытью и загрязнениям', 'Жууганга жана булганууга максималдуу туруктуулук'), l('Идеально для помещений с высокой нагрузкой', 'Жогорку жүктөмү бар бөлмөлөр үчүн идеалдуу')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Силиконовая формула', 'Силикон формуласы')
    }, {
      label: l('Покрытие', 'Каптоо'),
      value: l('Шелковисто-матовая', 'Жибектей жалтырабаган')
    }, {
      label: l('Плотность', 'Тыгыздыгы'),
      value: l('1.50 ± 0.05 г/см³')
    }, {
      label: l('Высыхание', 'Кургашы'),
      value: l('24 часа (полное)', '24 саат (толук)')
    }, {
      label: l('Стойкость', 'Туруктуулугу'),
      value: l('> 2000 циклов мытья', '> 2000 жолу жуу')
    }, {
      label: l('Класс', 'Классы'),
      value: l('PREMIUM')
    }],
    active: true,
    parentLine: 'velora'
  }, {
    id: 'velora-basic',
    name: l('VELORA BASIC'),
    category: l('Интерьерные краски', 'Интерьердик боёктор'),
    subtitle: l('Интерьерная краска оптимального качества', 'Оптималдуу сапаттагы интерьердик боёк'),
    price: 4600,
    unit: l('20кг'),
    image: "/VELORA_BASIC.jpg",
    description: l('VELORA BASIC — интерьерная краска с оптимальным соотношением цены и качества.', 'VELORA BASIC — баанын жана сапаттын оптималдуу катышы бар интерьердик боёк.'),
    advantages: [l('Оптимальное соотношение цены и качества', 'Баанын жана сапаттын оптималдуу катышы'), l('Хорошая укрывистость', 'Жакшы жабуучулук'), l('Матовое покрытие', 'Жалтырабаган каптоо'), l('Для стен и потолков', 'Дубалдар жана шыптар үчүн')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Стирол-акрил', 'Стирол-акрил')
    }, {
      label: l('Покрытие', 'Каптоо'),
      value: l('Матовая', 'Жалтырабаган')
    }, {
      label: l('Плотность', 'Тыгыздыгы'),
      value: l('1.45 ± 0.05 г/см³')
    }, {
      label: l('Высыхание', 'Кургашы'),
      value: l('24 часа', '24 саат')
    }, {
      label: l('Стойкость', 'Туруктуулугу'),
      value: l('> 1000 циклов мытья', '> 1000 жолу жуу')
    }, {
      label: l('Класс', 'Классы'),
      value: l('BASIC')
    }],
    active: true,
    parentLine: 'velora'
  }, {
    id: 'velora-eco',
    name: l('VELORA ECO'),
    category: l('Интерьерные краски', 'Интерьердик боёктор'),
    subtitle: l('Экономичная интерьерная краска', 'Үнөмдүү интерьердик боёк'),
    price: 3800,
    unit: l('20кг'),
    image: "/VELORA_ECO.jpg",
    description: l('VELORA ECO — экономичная интерьерная краска для бюджетного ремонта.', 'VELORA ECO — бюджеттик оңдоо үчүн үнөмдүү интерьердик боёк.'),
    advantages: [l('Доступная цена', 'Жеткиликтүү баа'), l('Хорошее качество покрытия', 'Каптоонун жакшы сапаты'), l('Экологически безопасная', 'Экологиялык коопсуз'), l('Для стандартного ремонта', 'Стандарттык оңдоо үчүн')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Акриловая', 'Акрил')
    }, {
      label: l('Покрытие', 'Каптоо'),
      value: l('Матовая', 'Жалтырабаган')
    }, {
      label: l('Плотность', 'Тыгыздыгы'),
      value: l('1.40 ± 0.05 г/см³')
    }, {
      label: l('Высыхание', 'Кургашы'),
      value: l('24 часа', '24 саат')
    }, {
      label: l('Класс', 'Классы'),
      value: l('ECO')
    }],
    active: true,
    parentLine: 'velora'
  }, {
    id: 'exterra',
    name: l('EXTERRA'),
    category: l('Фасадные краски', 'Фасаддык боёктор'),
    subtitle: l('Силиконовая фасадная краска', 'Силикон фасаддык боёгу'),
    price: 4800,
    unit: l('20кг'),
    image: "/EXTERRA_LUX.jpg",
    description: l('EXTERRA — высокопрочное защитно-декоративное покрытие для фасадов на основе стирол-акрилового сополимера с силиконовыми добавками.', 'EXTERRA — силикон кошумчалары менен стирол-акрил сополимеринин негизиндеги фасаддар үчүн жогорку бекемдиктеги коргоочу-декоративдик каптоо.'),
    advantages: [l('Водоотталкивающая формула с силиконом', 'Силикон менен сууну түртүүчү формула'), l('Высокая устойчивость к атмосферным воздействиям', 'Атмосфералык таасирлерге жогорку туруктуулук'), l('Эластичное, долговечное покрытие', 'Ийкемдүү, узакка чыдамдуу каптоо'), l('Защита от плесени и грибка', 'Көгөрүүдөн жана көзөнөктөн коргоо'), l('Доступна в версиях LUX, BASIC, ECO', 'LUX, BASIC, ECO версияларында жеткиликтүү')],
    specs: [],
    active: true
  }, {
    id: 'exterra-lux',
    name: l('EXTERRA LUX'),
    category: l('Фасадные краски', 'Фасаддык боёктор'),
    subtitle: l('Премиальная силиконовая фасадная краска', 'Премиум силикон фасаддык боёгу'),
    price: 5500,
    unit: l('20кг'),
    image: "/EXTERRA_LUX.jpg",
    description: l('EXTERRA LUX — высококачественная фасадная краска с повышенным содержанием силикона.', 'EXTERRA LUX — жогорулатылган силикон курамы бар жогорку сапаттагы фасаддык боёк.'),
    advantages: [l('Повышенное содержание силикона', 'Жогорулатылган силикон курамы'), l('Водоотталкивающий эффект', 'Сууну түртүүчү эффект'), l('Прочное матовое покрытие', 'Бекем жалтырабаган каптоо'), l('Максимальная защита от влаги', 'Нымдан максималдуу коргоо')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Силиконовая формула', 'Силикон формуласы')
    }, {
      label: l('Покрытие', 'Каптоо'),
      value: l('Матовая, прочная', 'Жалтырабаган, бекем')
    }, {
      label: l('Плотность', 'Тыгыздыгы'),
      value: l('1.50 ± 0.05 г/см³')
    }, {
      label: l('Свойства', 'Касиеттери'),
      value: l('Водоотталкивающая', 'Сууну түртүүчү')
    }, {
      label: l('Класс', 'Классы'),
      value: l('PREMIUM')
    }],
    active: true,
    parentLine: 'exterra'
  }, {
    id: 'exterra-basic',
    name: l('EXTERRA BASIC'),
    category: l('Фасадные краски', 'Фасаддык боёктор'),
    subtitle: l('Фасадная краска оптимального качества', 'Оптималдуу сапаттагы фасаддык боёк'),
    price: 4800,
    unit: l('20кг'),
    image: "/EXTERRA_BASIC.jpg",
    description: l('EXTERRA BASIC — фасадная краска с оптимальным соотношением цены и качества.', 'EXTERRA BASIC — баанын жана сапаттын оптималдуу катышы бар фасаддык боёк.'),
    advantages: [l('Оптимальное соотношение цены и качества', 'Баанын жана сапаттын оптималдуу катышы'), l('Хорошая атмосферостойкость', 'Жакшы атмосфера туруктуулугу'), l('Матовое покрытие', 'Жалтырабаган каптоо'), l('Для фасадов зданий', 'Имараттардын фасаддары үчүн')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Стирол-акрил + силикон', 'Стирол-акрил + силикон')
    }, {
      label: l('Покрытие', 'Каптоо'),
      value: l('Матовая', 'Жалтырабаган')
    }, {
      label: l('Плотность', 'Тыгыздыгы'),
      value: l('1.45 ± 0.05 г/см³')
    }, {
      label: l('Свойства', 'Касиеттери'),
      value: l('Водоотталкивающая', 'Сууну түртүүчү')
    }, {
      label: l('Класс', 'Классы'),
      value: l('BASIC')
    }],
    active: true,
    parentLine: 'exterra'
  }, {
    id: 'exterra-eco',
    name: l('EXTERRA ECO'),
    category: l('Фасадные краски', 'Фасаддык боёктор'),
    subtitle: l('Экономичная фасадная краска', 'Үнөмдүү фасаддык боёк'),
    price: 4000,
    unit: l('20кг'),
    image: "/EXTERRA_ECO.jpg",
    description: l('EXTERRA ECO — экономичная фасадная краска для бюджетных проектов.', 'EXTERRA ECO — бюджеттик долбоорлор үчүн үнөмдүү фасаддык боёк.'),
    advantages: [l('Доступная цена', 'Жеткиликтүү баа'), l('Базовая защита фасада', 'Фасаддын базалык коргоосу'), l('Экологически безопасная', 'Экологиялык коопсуз'), l('Для стандартных объектов', 'Стандарттык объекттер үчүн')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Акриловая', 'Акрил')
    }, {
      label: l('Покрытие', 'Каптоо'),
      value: l('Матовая', 'Жалтырабаган')
    }, {
      label: l('Плотность', 'Тыгыздыгы'),
      value: l('1.40 ± 0.05 г/см³')
    }, {
      label: l('Класс', 'Классы'),
      value: l('ECO')
    }],
    active: true,
    parentLine: 'exterra'
  }, {
    id: 'microlux',
    name: l('MICROLUX'),
    category: l('Декоративные покрытия', 'Декоративдик каптоолор'),
    subtitle: l('Микроцементная декоративная краска', 'Микроцемент декоративдик боёгу'),
    price: 0,
    unit: l('20кг'),
    image: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/hyseuuq2warchstz2tvf3s/f7c74e3b-3f4e-439c-8223-13536fb8e81c/MIKROLUX.jpg",
    description: l('MICROLUX — декоративная краска с эффектом микроцемента для создания современных интерьеров.', 'MICROLUX — заманбап интерьерлерди түзүү үчүн микроцемент эффектиси бар декоративдик боёк.'),
    advantages: [l('Эффект натурального бетона', 'Табигый бетон эффектиси'), l('Современный индустриальный стиль', 'Заманбап индустриалдык стиль'), l('Высокая прочность покрытия', 'Каптоонун жогорку бекемдиги'), l('Для стен, полов и мебели', 'Дубалдар, полдор жана эмерек үчүн')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Микроцемент', 'Микроцемент')
    }, {
      label: l('Покрытие', 'Каптоо'),
      value: l('Текстурная', 'Текстуралуу')
    }, {
      label: l('Эффект', 'Эффект'),
      value: l('Бетон / камень', 'Бетон / таш')
    }, {
      label: l('Применение', 'Колдонуу'),
      value: l('Стены, полы, мебель', 'Дубалдар, полдор, эмерек')
    }],
    active: true
  }, {
    id: 'granite-line',
    name: l('NEO STONE'),
    category: l('Декоративные покрытия', 'Декоративдик каптоолор'),
    subtitle: l('Жидкий гранит — декоративное покрытие', 'Суюк гранит — декоративдик каптоо'),
    price: 0,
    unit: l('25кг'),
    image: "/GRANITE_LINE.jpg",
    description: l('NEO STONE — декоративное покрытие с эффектом натурального камня.', 'NEO STONE — табигый таш эффектиси бар декоративдик каптоо.'),
    advantages: [l('Эффект натурального камня', 'Табигый таш эффектиси'), l('Высокая износостойкость', 'Жогорку тозууга чыдамдуулук'), l('Для фасадов и интерьеров', 'Фасаддар жана интерьерлер үчүн'), l('Уникальная текстура', 'Уникалдуу текстура')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Акриловая с гранитной крошкой', 'Гранит кесиндиси менен акрил')
    }, {
      label: l('Покрытие', 'Каптоо'),
      value: l('Текстурная', 'Текстуралуу')
    }, {
      label: l('Эффект', 'Эффект'),
      value: l('Натуральный камень', 'Табигый таш')
    }],
    active: true
  }, {
    id: 'storm-fixera-primer',
    name: l('STORM FIXERA PRIMER'),
    category: l('Грунтовки и праймеры', 'Грунтовкалар жана праймерлер'),
    subtitle: l('Универсальный грунт с кварцевым песком', 'Кварц кумдуу универсалдуу грунт'),
    price: 2650,
    unit: l('20кг'),
    image: "https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/hyseuuq2warchstz2tvf3s/f7c74e3b-3f4e-439c-8223-13536fb8e81c/STORM_FIXERA_PRIMER.jpg",
    description: l('STORM FIXERA PRIMER — универсальный грунт с добавлением кварцевого песка для подготовки стен.', 'STORM FIXERA PRIMER — дубалдарды даярдоо үчүн кварц кумун кошуу менен универсалдуу грунт.'),
    advantages: [l('Кварцевый песок для лучшей адгезии', 'Жакшыраак адгезия үчүн кварц куму'), l('Укрепляет поверхность', 'Бетти бекемдейт'), l('Для внутренних и наружных работ', 'Ички жана сырткы иштер үчүн'), l('Экономичный расход', 'Үнөмдүү чыгым')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Акриловая с кварцевым песком', 'Кварц кумдуу акрил')
    }, {
      label: l('Применение', 'Колдонуу'),
      value: l('Внутренние и наружные работы', 'Ички жана сырткы иштер')
    }, {
      label: l('Расход', 'Чыгым'),
      value: l('150-200 г/м²')
    }, {
      label: l('Высыхание', 'Кургашы'),
      value: l('4-6 часов', '4-6 саат')
    }],
    active: true
  }, {
    id: 'calm-fixera-primer',
    name: l('CALM FIXERA PRIMER'),
    category: l('Грунтовки и праймеры', 'Грунтовкалар жана праймерлер'),
    subtitle: l('Акриловый состав для ухода за бетоном', 'Бетонду кароо үчүн акрил курамы'),
    price: 2400,
    unit: l('20кг'),
    image: "/CALM_FIXERA_PRIMER.jpg",
    description: l('CALM FIXERA PRIMER — акриловый состав для защиты и ухода за бетонными поверхностями.', 'CALM FIXERA PRIMER — бетон беттерди коргоо жана кароо үчүн акрил курамы.'),
    advantages: [l('Защита бетонных поверхностей', 'Бетон беттерди коргоо'), l('Предотвращает образование трещин', 'Жаракалардын пайда болушун алдын алат'), l('Улучшает структуру поверхности', 'Беттин структурасын жакшыртат'), l('Экологически безопасный', 'Экологиялык коопсуз')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Акриловая', 'Акрил')
    }, {
      label: l('Применение', 'Колдонуу'),
      value: l('Бетонные поверхности', 'Бетон беттер')
    }, {
      label: l('Расход', 'Чыгым'),
      value: l('100-150 г/м²')
    }, {
      label: l('Высыхание', 'Кургашы'),
      value: l('2-4 часа', '2-4 саат')
    }],
    active: true
  }, {
    id: 'quarzo-bond',
    name: l('QUARZO BOND'),
    category: l('Грунтовки и праймеры', 'Грунтовкалар жана праймерлер'),
    subtitle: l('Бетоноконтакт с кварцевым наполнителем', 'Кварц толтургучу менен бетоноконтакт'),
    price: 1900,
    unit: l('20кг'),
    image: "/QUARZO_BOND.jpg",
    description: l('QUARZO BOND — профессиональный грунт для подготовки гладких и плотных оснований.', 'QUARZO BOND — жылмакай жана тыгыз негиздерди даярдоо үчүн кесипкөй грунт.'),
    advantages: [l('Для гладких оснований', 'Жылмакай негиздер үчүн'), l('Кварцевый наполнитель', 'Кварц толтургучу'), l('Создаёт шероховатую поверхность', 'Оройлуу бетти түзөт'), l('Быстрое высыхание', 'Тез кургашы')],
    specs: [{
      label: l('Основа', 'Негизи'),
      value: l('Акриловая с кварцем', 'Кварц менен акрил')
    }, {
      label: l('Применение', 'Колдонуу'),
      value: l('Гладкие основания', 'Жылмакай негиздер')
    }, {
      label: l('Расход', 'Чыгым'),
      value: l('200-300 г/м²')
    }, {
      label: l('Высыхание', 'Кургашы'),
      value: l('12 часов', '12 саат')
    }],
    active: true
  }],
  stats: [{
    value: l('11+'),
    label: l('Лет на рынке', 'Жыл рынокто'),
    delay: 0
  }, {
    value: l('100%'),
    label: l('Собственное производство', 'Өздүк өндүрүш'),
    delay: 0.1
  }, {
    value: l('50+'),
    label: l('Партнёров по всей стране', 'Өлкө боюнча өнөктөштөр'),
    delay: 0.2
  }, {
    value: l('1000+'),
    label: l('Реализованных проектов', 'Ишке ашырылган долбоорлор'),
    delay: 0.3
  }],
  map: {
    lat: '42.8746',
    lng: '74.5698',
    zoom: 16,
    addressLabel: l('г. Бишкек, ул. Ю. Фучика 9а', 'Бишкек ш., Ю. Фучик көч. 9а')
  },
  applicationAreas: [{
    id: 'a1',
    icon: 'Building2',
    title: l('Жилые комплексы', 'Турак жай комплекстери'),
    description: l('Фасадные и интерьерные решения для многоэтажного строительства.', 'Көп кабаттуу курулуш үчүн фасаддык жана интерьердик чечимдер.')
  }, {
    id: 'a2',
    icon: 'Home',
    title: l('Частное строительство', 'Жеке курулуш'),
    description: l('Краски и покрытия для коттеджей и частных домов.', 'Коттедждер жана жеке үйлөр үчүн боёктор жана каптоолор.')
  }, {
    id: 'a3',
    icon: 'Store',
    title: l('Коммерческие объекты', 'Коммерциялык объекттер'),
    description: l('Долговечные покрытия для торговых и офисных помещений.', 'Соода жана кеңсе жайлары үчүн узакка чыдамдуу каптоолор.')
  }, {
    id: 'a4',
    icon: 'Landmark',
    title: l('Государственные учреждения', 'Мамлекеттик мекемелер'),
    description: l('Сертифицированные материалы для социальных объектов.', 'Социалдык объекттер үчүн сертификатталган материалдар.')
  }, {
    id: 'a5',
    icon: 'Factory',
    title: l('Промышленные объекты', 'Өнөр жай объекттери'),
    description: l('Защитные покрытия для производственных помещений.', 'Өндүрүш жайлары үчүн коргоочу каптоолор.')
  }, {
    id: 'a6',
    icon: 'Palette',
    title: l('Дизайн интерьеров', 'Интерьер дизайны'),
    description: l('Декоративные покрытия для уникальных интерьерных решений.', 'Уникалдуу интерьердик чечимдер үчүн декоративдик каптоолор.')
  }],
  partnerBenefits: [{
    id: 'b1',
    icon: 'TrendingUp',
    title: l('Выгодные цены', 'Пайдалуу баалар'),
    description: l('Специальные дилерские цены и гибкая система скидок.', 'Атайын дилердик баалар жана ийкемдүү арзандатуу системасы.')
  }, {
    id: 'b2',
    icon: 'Truck',
    title: l('Логистика', 'Логистика'),
    description: l('Бесплатная доставка от определённого объёма заказа.', 'Белгилүү көлөмдөгү заказдан бекер жеткирүү.')
  }, {
    id: 'b3',
    icon: 'HeadphonesIcon',
    title: l('Поддержка', 'Колдоо'),
    description: l('Персональный менеджер и техническая поддержка.', 'Жеке менеджер жана техникалык колдоо.')
  }, {
    id: 'b4',
    icon: 'BarChart2',
    title: l('Маркетинг', 'Маркетинг'),
    description: l('Рекламные материалы и совместные акции.', 'Жарнамалык материалдар жана биргелешкен акциялар.')
  }],
  partnerSteps: [{
    id: 's1',
    num: '01',
    title: l('Заявка', 'Арыз'),
    desc: l('Заполните форму или позвоните нам.', 'Форманы толтуруңуз же бизге чалыңыз.')
  }, {
    id: 's2',
    num: '02',
    title: l('Обсуждение', 'Талкуулоо'),
    desc: l('Обсудим условия сотрудничества.', 'Кызматташтык шарттарын талкуулайбыз.')
  }, {
    id: 's3',
    num: '03',
    title: l('Договор', 'Келишим'),
    desc: l('Подписываем договор партнёрства.', 'Өнөктөштүк келишимине кол коёбуз.')
  }, {
    id: 's4',
    num: '04',
    title: l('Работа', 'Иш'),
    desc: l('Начинаем поставки продукции.', 'Продукцияны жеткирүүнү баштайбыз.')
  }],
  partnerPage: {
    heroLabel: l('BM PARTNER', 'BM PARTNER'),
    heroTitle: l('Станьте партнёром', 'Өнөктөш болуңуз'),
    heroTitleAccent: l('YAVUZ BURAK', 'YAVUZ BURAK'),
    heroDescription: l('Присоединяйтесь к нашей дилерской сети и получите доступ к премиальным лакокрасочным материалам на выгодных условиях.', 'Биздин дилердик тармакка кошулуңуз жана пайдалуу шарттарда премиум лак-боёк материалдарына жетүү мүмкүнчүлүгүн алыңыз.'),
    benefitsLabel: l('Преимущества', 'Артыкчылыктар'),
    benefitsTitle: l('Почему выбирают', 'Эмне үчүн тандашат'),
    benefitsTitleAccent: l('BM PARTNER', 'BM PARTNER'),
    stepsLabel: l('Как начать', 'Кантип баштоо'),
    stepsTitle: l('Путь к', 'Жол'),
    stepsTitleAccent: l('партнёрству', 'өнөктөштүккө'),
    formLabel: l('Заявка', 'Арыз'),
    formTitle: l('Стать', 'Болуу'),
    formTitleAccent: l('партнёром', 'өнөктөш'),
    formDescription: l('Заполните форму и наш менеджер свяжется с вами в течение 2 рабочих дней.', 'Форманы толтуруңуз, биздин менеджер 2 жумуш күнүнүн ичинде сиз менен байланышат.')
  },
  partnerSection: {
    label: l('Партнёрская программа', 'Өнөктөштүк программасы'),
    title: l('Присоединяйтесь к', 'Кошулуңуз'),
    titleAccent: l('BM PARTNER', 'BM PARTNER'),
    description: l('Станьте частью нашей дилерской сети и получите доступ к премиальным продуктам на выгодных условиях.', 'Биздин дилердик тармактын бир бөлүгү болуңуз жана пайдалуу шарттарда премиум продуктуларга жетүү мүмкүнчүлүгүн алыңыз.'),
    ctaText: l('Узнать подробнее', 'Кененирээк билүү')
  },
  teamMembers: [{
    id: 't1',
    name: l('Бурак Явуз', 'Бурак Явуз'),
    role: l('Основатель и директор', 'Негиздөөчү жана директор'),
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=400'
  }, {
    id: 't2',
    name: l('Айбек Токтосунов', 'Айбек Токтосунов'),
    role: l('Технический директор', 'Техникалык директор'),
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=400'
  }, {
    id: 't3',
    name: l('Нурлан Исаков', 'Нурлан Исаков'),
    role: l('Руководитель производства', 'Өндүрүш жетекчиси'),
    image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=400'
  }, {
    id: 't4',
    name: l('Алия Сатыбалдиева', 'Алия Сатыбалдиева'),
    role: l('Менеджер по продажам', 'Сатуу менеджери'),
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=400'
  }]
};

// Migration helper: converts old string fields to LocalizedString
function migrateString(val: any): LocalizedString {
  if (typeof val === 'string') {
    return {
      ru: val,
      ky: val
    };
  }
  if (val && typeof val === 'object' && 'ru' in val && 'ky' in val) {
    return val as LocalizedString;
  }
  return {
    ru: '',
    ky: ''
  };
}
function migrateProduct(p: any): Product {
  return {
    ...p,
    name: migrateString(p.name),
    subtitle: migrateString(p.subtitle),
    category: migrateString(p.category || {
      ru: 'Без категории',
      ky: 'Категориясыз'
    }),
    unit: migrateString(p.unit),
    description: migrateString(p.description),
    advantages: (p.advantages || []).map((a: any) => migrateString(a)),
    specs: (p.specs || []).map((s: any) => ({
      label: migrateString(s.label),
      value: migrateString(s.value)
    })),
    active: p.active !== undefined ? p.active : true,
    parentLine: p.parentLine || undefined
  };
}
function migratePreviewProduct(p: any): PreviewProduct {
  return {
    ...p,
    name: migrateString(p.name),
    category: migrateString(p.category),
    description: migrateString(p.description)
  };
}
function migrateStat(s: any): StatItem {
  return {
    ...s,
    value: migrateString(s.value),
    label: migrateString(s.label)
  };
}
function migrateArea(a: any): AreaBlock {
  return {
    ...a,
    title: migrateString(a.title),
    description: migrateString(a.description)
  };
}
function migrateBenefit(b: any): PartnerBenefit {
  return {
    ...b,
    title: migrateString(b.title),
    description: migrateString(b.description)
  };
}
function migrateStep(s: any): PartnerStep {
  return {
    ...s,
    title: migrateString(s.title),
    desc: migrateString(s.desc)
  };
}
function migrateTeamMember(m: any): TeamMember {
  return {
    ...m,
    name: migrateString(m.name),
    role: migrateString(m.role)
  };
}
export function getContent(): ContentStore {
  try {
    const stored = localStorage.getItem('yb_content');
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        products: (parsed.products || DEFAULT_CONTENT.products).map(migrateProduct),
        previewProducts: (parsed.previewProducts || DEFAULT_CONTENT.previewProducts).map(migratePreviewProduct),
        stats: (parsed.stats || DEFAULT_CONTENT.stats).map(migrateStat),
        map: {
          ...DEFAULT_CONTENT.map,
          ...parsed.map,
          addressLabel: migrateString(parsed.map?.addressLabel || DEFAULT_CONTENT.map.addressLabel)
        },
        applicationAreas: (parsed.applicationAreas || DEFAULT_CONTENT.applicationAreas).map(migrateArea),
        partnerBenefits: (parsed.partnerBenefits || DEFAULT_CONTENT.partnerBenefits).map(migrateBenefit),
        partnerSteps: (parsed.partnerSteps || DEFAULT_CONTENT.partnerSteps).map(migrateStep),
        partnerPage: {
          heroLabel: migrateString(parsed.partnerPage?.heroLabel || DEFAULT_CONTENT.partnerPage.heroLabel),
          heroTitle: migrateString(parsed.partnerPage?.heroTitle || DEFAULT_CONTENT.partnerPage.heroTitle),
          heroTitleAccent: migrateString(parsed.partnerPage?.heroTitleAccent || DEFAULT_CONTENT.partnerPage.heroTitleAccent),
          heroDescription: migrateString(parsed.partnerPage?.heroDescription || DEFAULT_CONTENT.partnerPage.heroDescription),
          benefitsLabel: migrateString(parsed.partnerPage?.benefitsLabel || DEFAULT_CONTENT.partnerPage.benefitsLabel),
          benefitsTitle: migrateString(parsed.partnerPage?.benefitsTitle || DEFAULT_CONTENT.partnerPage.benefitsTitle),
          benefitsTitleAccent: migrateString(parsed.partnerPage?.benefitsTitleAccent || DEFAULT_CONTENT.partnerPage.benefitsTitleAccent),
          stepsLabel: migrateString(parsed.partnerPage?.stepsLabel || DEFAULT_CONTENT.partnerPage.stepsLabel),
          stepsTitle: migrateString(parsed.partnerPage?.stepsTitle || DEFAULT_CONTENT.partnerPage.stepsTitle),
          stepsTitleAccent: migrateString(parsed.partnerPage?.stepsTitleAccent || DEFAULT_CONTENT.partnerPage.stepsTitleAccent),
          formLabel: migrateString(parsed.partnerPage?.formLabel || DEFAULT_CONTENT.partnerPage.formLabel),
          formTitle: migrateString(parsed.partnerPage?.formTitle || DEFAULT_CONTENT.partnerPage.formTitle),
          formTitleAccent: migrateString(parsed.partnerPage?.formTitleAccent || DEFAULT_CONTENT.partnerPage.formTitleAccent),
          formDescription: migrateString(parsed.partnerPage?.formDescription || DEFAULT_CONTENT.partnerPage.formDescription)
        },
        partnerSection: {
          label: migrateString(parsed.partnerSection?.label || DEFAULT_CONTENT.partnerSection.label),
          title: migrateString(parsed.partnerSection?.title || DEFAULT_CONTENT.partnerSection.title),
          titleAccent: migrateString(parsed.partnerSection?.titleAccent || DEFAULT_CONTENT.partnerSection.titleAccent),
          description: migrateString(parsed.partnerSection?.description || DEFAULT_CONTENT.partnerSection.description),
          ctaText: migrateString(parsed.partnerSection?.ctaText || DEFAULT_CONTENT.partnerSection.ctaText)
        },
        teamMembers: (parsed.teamMembers || DEFAULT_CONTENT.teamMembers).map(migrateTeamMember),
        hero: {
          subtitle: migrateString(parsed.hero?.subtitle || DEFAULT_CONTENT.hero.subtitle),
          title2: migrateString(parsed.hero?.title2 || DEFAULT_CONTENT.hero.title2),
          description: migrateString(parsed.hero?.description || DEFAULT_CONTENT.hero.description),
          btnCatalog: migrateString(parsed.hero?.btnCatalog || DEFAULT_CONTENT.hero.btnCatalog),
          btnContact: migrateString(parsed.hero?.btnContact || DEFAULT_CONTENT.hero.btnContact),
          hint: migrateString(parsed.hero?.hint || DEFAULT_CONTENT.hero.hint)
        },
        about: {
          label: migrateString(parsed.about?.label || DEFAULT_CONTENT.about.label),
          title1: migrateString(parsed.about?.title1 || DEFAULT_CONTENT.about.title1),
          title2: migrateString(parsed.about?.title2 || DEFAULT_CONTENT.about.title2),
          description: migrateString(parsed.about?.description || DEFAULT_CONTENT.about.description),
          image: parsed.about?.image || DEFAULT_CONTENT.about.image,
          features: (parsed.about?.features || DEFAULT_CONTENT.about.features).map((f: any) => ({
            ...f,
            title: migrateString(f.title),
            text: migrateString(f.text)
          })),
          teamTitle1: migrateString(parsed.about?.teamTitle1 || DEFAULT_CONTENT.about.teamTitle1),
          teamTitle2: migrateString(parsed.about?.teamTitle2 || DEFAULT_CONTENT.about.teamTitle2),
          teamDesc: migrateString(parsed.about?.teamDesc || DEFAULT_CONTENT.about.teamDesc)
        },
        catalogSection: {
          label: migrateString(parsed.catalogSection?.label || DEFAULT_CONTENT.catalogSection.label),
          title1: migrateString(parsed.catalogSection?.title1 || DEFAULT_CONTENT.catalogSection.title1),
          title2: migrateString(parsed.catalogSection?.title2 || DEFAULT_CONTENT.catalogSection.title2)
        },
        areasSection: {
          label: migrateString(parsed.areasSection?.label || DEFAULT_CONTENT.areasSection.label),
          title1: migrateString(parsed.areasSection?.title1 || DEFAULT_CONTENT.areasSection.title1),
          title2: migrateString(parsed.areasSection?.title2 || DEFAULT_CONTENT.areasSection.title2)
        },
        clients: parsed.clients || DEFAULT_CONTENT.clients
      };
    }
  } catch {}
  return DEFAULT_CONTENT;
}
export function saveContent(content: ContentStore): void {
  localStorage.setItem('yb_content', JSON.stringify(content));
}
export function getDefaultContent(): ContentStore {
  return DEFAULT_CONTENT;
}